'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

const ADMIN_PASSWORD = 'beskow';

interface AdminPasswordPromptProps {
  onDismiss: () => void;
}

export default function AdminPasswordPrompt({ onDismiss }: AdminPasswordPromptProps) {
  const [password, setPassword] = useState('');
  const [shake, setShake] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    // Focus input on mount
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      onDismiss();
      router.push('/admin');
    } else {
      setShake(true);
      setPassword('');
      setTimeout(() => {
        setShake(false);
        onDismiss();
      }, 600);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onDismiss();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(245, 237, 224, 0.85)',
        backdropFilter: 'blur(8px)',
      }}
      onClick={onDismiss}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'rgba(255, 252, 247, 0.97)',
          border: '1.5px solid rgba(180, 155, 130, 0.35)',
          borderRadius: '20px',
          padding: '40px 48px',
          boxShadow: '0 8px 40px rgba(140, 100, 70, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          minWidth: '300px',
          animation: shake ? 'adminShake 0.5s ease' : undefined,
        }}
      >
        <style>{`
          @keyframes adminShake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
        `}</style>
        <div style={{ fontSize: '28px', lineHeight: 1 }}>🌿</div>
        <p style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: '15px',
          fontWeight: 300,
          fontStyle: 'italic',
          color: 'rgba(100, 75, 55, 0.8)',
          margin: 0,
          letterSpacing: '0.03em',
        }}>
          Enter the secret word
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <input
            ref={inputRef}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="···"
            autoComplete="off"
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '10px',
              border: '1.5px solid rgba(180, 155, 130, 0.4)',
              background: 'rgba(250, 246, 240, 0.9)',
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '16px',
              color: '#5a3e2b',
              outline: 'none',
              textAlign: 'center',
              letterSpacing: '0.1em',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              border: 'none',
              background: 'rgba(160, 120, 90, 0.15)',
              color: 'rgba(100, 70, 45, 0.9)',
              fontFamily: "'Fraunces', Georgia, serif",
              fontSize: '14px',
              fontStyle: 'italic',
              cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  );
}
